const AMBIGUOUS_STRONG_CLOSING = /(?<![\\*])\*\*(?=\S)([^*\r\n]*?[\p{P}\p{S}])\*\*(?=[^\s\p{P}\p{S}*])/gu

function normalizeStrongClosings(text: string): string {
  return text.replace(AMBIGUOUS_STRONG_CLOSING, match => `${match}<!-- -->`)
}

function normalizeOutsideInlineCode(text: string): string {
  let result = ''
  let plainStart = 0
  let index = 0

  while (index < text.length) {
    if (text[index] !== '`') {
      index += 1
      continue
    }

    let runEnd = index + 1
    while (text[runEnd] === '`') runEnd += 1
    const delimiterLength = runEnd - index
    let closingStart = -1
    let searchFrom = runEnd

    while (searchFrom < text.length) {
      const candidate = text.indexOf('`', searchFrom)
      if (candidate === -1) break

      let candidateEnd = candidate + 1
      while (text[candidateEnd] === '`') candidateEnd += 1
      if (candidateEnd - candidate === delimiterLength) {
        closingStart = candidate
        break
      }
      searchFrom = candidateEnd
    }

    if (closingStart === -1) {
      index = runEnd
      continue
    }

    result += normalizeStrongClosings(text.slice(plainStart, index))
    const closingEnd = closingStart + delimiterLength
    result += text.slice(index, closingEnd)
    plainStart = closingEnd
    index = closingEnd
  }

  return result + normalizeStrongClosings(text.slice(plainStart))
}

/**
 * Makes common AI-authored Markdown parse consistently without changing the
 * visible text. Code fences and inline code are kept byte-for-byte intact.
 */
export function normalizeMarkdownCompatibility(text: string): string {
  let result = ''
  let plainStart = 0
  let lineStart = 0
  let fence: { marker: string; length: number; start: number } | null = null

  while (lineStart <= text.length) {
    const newlineIndex = text.indexOf('\n', lineStart)
    const rawLineEnd = newlineIndex === -1 ? text.length : newlineIndex
    const lineEnd = rawLineEnd > lineStart && text[rawLineEnd - 1] === '\r'
      ? rawLineEnd - 1
      : rawLineEnd
    const line = text.slice(lineStart, lineEnd)
    const nextLineStart = newlineIndex === -1 ? text.length : newlineIndex + 1

    if (fence) {
      const closingMatch = line.match(/^ {0,3}(`+|~+)[ \t]*$/)
      if (closingMatch?.[1]
        && closingMatch[1][0] === fence.marker
        && closingMatch[1].length >= fence.length
      ) {
        result += text.slice(fence.start, nextLineStart)
        plainStart = nextLineStart
        fence = null
      }
    } else {
      const openingMatch = line.match(/^ {0,3}(`{3,}|~{3,})(.*)$/)
      if (openingMatch?.[1] && openingMatch[2] != null) {
        const delimiter = openingMatch[1]
        const isValidBacktickInfo = delimiter[0] !== '`' || !openingMatch[2].includes('`')
        if (isValidBacktickInfo) {
          result += normalizeOutsideInlineCode(text.slice(plainStart, lineStart))
          fence = {
            marker: delimiter[0]!,
            length: delimiter.length,
            start: lineStart,
          }
        }
      }
    }

    if (newlineIndex === -1) break
    lineStart = nextLineStart
  }

  if (fence) return result + text.slice(fence.start)
  return result + normalizeOutsideInlineCode(text.slice(plainStart))
}
